import { useContext, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import { faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import { toggleArray } from '@domain-group/javascript-utils'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'
import type { DefaultContext } from '@contexts/pageContext'

import useAutoOrderQueues, { AutoOrderQueueFilters } from '@hooks/useAutoOrderQueues'
import useAutoOrderQueue, { generateUrls } from '@hooks/useAutoOrderQueue'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import AutoOrderQueueTable from '../AutoOrderQueuesTable'

import type { PageContextPayload } from '../../index'

import { AutoOrderQueueStatusModel } from '@models/types'

import useAutoOrderQueueStatuses from '@hooks/useAutoOrderQueueStatuses'

import type { ResolveUnhealthyAutoOrderQueuesResult } from '@redux/modules/autoOrderQueue'

type BuildPageFilterFieldParams = {
  autoOrderQueueStatuses: AutoOrderQueueStatusModel[],
}

const buildPageFilterFields = (params: BuildPageFilterFieldParams) => {
  const { autoOrderQueueStatuses } = params

  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        placeholder: 'Search by Auto Order Queue ID',
        style,
      },
      key: 'id',
      label: 'Search',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        placeholder: 'Reference ID',
        style,
      },
      key: 'referenceId',
      label: 'Reference ID',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'status',
      label: 'Status',
      values: autoOrderQueueStatuses.map(autoOrderQueueStatus => (
        { key: autoOrderQueueStatus.name, label: autoOrderQueueStatus.displayName }
      )),
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'startDate',
      label: 'Start Date',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'endDate',
      label: 'End Date',
    },
  ]
}

const showResolveResultToasts = (data: ResolveUnhealthyAutoOrderQueuesResult) => {
  const { successCount, skippedCount, errorCount } = data

  if (successCount > 0) {
    toast.success(`${successCount} AOQ(s) resolved successfully`)
  }

  if (skippedCount > 0) {
    toast.warn(`${skippedCount} AOQ(s) need investigation`)
  }

  if (errorCount > 0) {
    toast.error(`${errorCount} AOQ(s) encountered errors`)
  }

  // Show individual messages for non-success results
  data.results
    .filter(r => !r.success)
    .forEach(r => {
      toast.info(`AOQ #${r.autoOrderQueueId}: ${r.message}`)
    })
}

type ToggleSelectedAutoOrderQueueIdParams = {
  autoOrderQueueId: number,
  selectedAutoOrderQueueIds: number[],
  setSelectedAutoOrderQueueIds: React.Dispatch<React.SetStateAction<number[]>>,
}

const toggleSelectedAutoOrderQueueId = (params: ToggleSelectedAutoOrderQueueIdParams) => {
  const { autoOrderQueueId, selectedAutoOrderQueueIds, setSelectedAutoOrderQueueIds } = params

  const updatedIds: number[] = toggleArray(selectedAutoOrderQueueIds, autoOrderQueueId)

  setSelectedAutoOrderQueueIds(updatedIds)

  return updatedIds
}

interface PageContentProps {
  pageFilters: AutoOrderQueueFilters,
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { autoOrderQueueStatuses } = useAutoOrderQueueStatuses({ performHttpRequests: true })

  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const autoOrderQueuesPayload = useAutoOrderQueues({
    filters: {
      ...pageFilters,
    },
    performHttpRequests: true,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAutoOrderQueues,
    filteredAutoOrderQueuesCount,
    hasFilteredAutoOrderQueues,
    loading,
  } = autoOrderQueuesPayload

  const {
    callbacks: {
      resolveUnhealthyAutoOrderQueues,
    },
    creating,
  } = useAutoOrderQueue()

  const { autoOrderQueuesIndexUrl } = generateUrls()

  const isFailedFilter = pageFilters.status === 'Failed'

  const [selectedAutoOrderQueueIds, setSelectedAutoOrderQueueIds] = useState<number[]>([])

  const hasSelectedAutoOrderQueues = !!selectedAutoOrderQueueIds.length

  const filteredAutoOrderQueueIds = filteredAutoOrderQueues.map(aoq => aoq.id)
  const hasSelectedAllAutoOrderQueues = selectedAutoOrderQueueIds.length >= filteredAutoOrderQueuesCount

  const handleResolve = async () => {
    const result = await resolveUnhealthyAutoOrderQueues(selectedAutoOrderQueueIds)

    if (result.success) {
      showResolveResultToasts(result.data)
      setSelectedAutoOrderQueueIds([])
    }
  }

  return (
    <>
      <Helmet>
        <title>Auto Order Queues | AIM Digital</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="active"
        tabBarItems={[
          {
            href: autoOrderQueuesIndexUrl,
            icon: faCheckCircle,
            key: 'active',
            title: 'Active',
          },
        ]}
        title="Auto Order Queues"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredAutoOrderQueues && <ListBlankState />}

            {!loading && hasFilteredAutoOrderQueues && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Auto Order Queues">
                <Box flexDirection="column">
                  <AutoOrderQueueTable
                    autoOrderQueuesPayload={autoOrderQueuesPayload}
                    callbacks={{
                      toggleAllAutoOrderQueues: () => setSelectedAutoOrderQueueIds(hasSelectedAllAutoOrderQueues ? [] : filteredAutoOrderQueueIds),
                      toggleSelectedAutoOrderQueueId: (autoOrderQueueId: number) => toggleSelectedAutoOrderQueueId(
                        { autoOrderQueueId, selectedAutoOrderQueueIds, setSelectedAutoOrderQueueIds },
                      ),
                    }}
                    hasSelectedAllAutoOrderQueues={hasSelectedAllAutoOrderQueues}
                    selectedAutoOrderQueueIds={selectedAutoOrderQueueIds}
                    showSelection={isFailedFilter}
                  />
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredAutoOrderQueuesCount}
                  loading={loading}
                >
                  {isFailedFilter && (
                    <div>
                      <Button
                        buttonStyle="secondaryCreate"
                        disabled={!hasSelectedAutoOrderQueues}
                        loading={creating}
                        onClick={handleResolve}
                        size={['medium', 'small']}
                      >
                        Resolve Failed AOQs
                      </Button>
                    </div>
                  )}
                </DashboardModule.LoadMoreFooter>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              filterFields={buildPageFilterFields({ autoOrderQueueStatuses })}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
